import { getLocale, getServerCatalog, registerServerFallback, t } from "./i18n";

/**
 * Texte, die der Server als fertige deutsche Sätze liefert oder speichert (Haltegründe, Prüfhinweise, Verlauf,
 * Fehlermeldungen), beim Anzeigen übersetzen – auch für bestehende Einträge, ohne die Datenbank umzubauen.
 *
 * 1. Katalog src/locales/server-en.json: automatisch aus dem Servercode eingesammelt (scripts/server-texts.mjs,
 *    CI prüft Vollständigkeit). Muster mit {0}, {1} … – die Werte (SKU, Mengen, Gründe) werden übernommen und,
 *    falls selbst ein bekannter Servertext, ebenfalls übersetzt.
 * 2. Feste Anfänge (unten) für Texte, die der Server aus Teilen zusammensetzt (z. B. „Fehlbestand: A (benötigt 2, frei 0), B …“).
 * Eigene Regelnamen der Kunden und unbekannte Texte bleiben unverändert.
 */
const PREFIX: [RegExp, string][] = [
  [/^In Prüfung bei Logiful \(Adresse\)$/, "Being checked by Logiful (address)"],
  [/^Adresse prüfen: (.*)$/, "Check address: $1"],
  [/^Unbekannte Artikel(.*)$/, "Unknown products$1"],
  [/^Stammdaten unvollständig(.*)$/, "Master data incomplete$1"],
  [/^Beilage out of stock(.*)$/, "Insert out of stock$1"],
  [/^Fehlbestand(.*)$/, "Insufficient stock$1"],
  [/^Vorgegebene Charge(.*)$/, "Requested batch$1"],
  [/^Händler-Vorgabe(.*)$/, "Merchant requirement$1"],
  [/^Amazon-Etiketten fehlen(.*)$/, "Amazon labels missing$1"],
  [/^Ausfuhrbegleitdokument fehlt: Ausfuhr nach (\w+) \((.*)\) – Dokument im Auftrag hochladen und freigeben$/, "Export document missing: export to $1 ($2) – upload and approve the document in the order"],
  [/^Ausfuhrbegleitdokument wartet auf Freigabe(.*)$/, "Export document awaiting approval – please review and approve it in the order"],
  [/^Würde abgelehnt(.*)$/, "Would be rejected$1"],
];
/** Teile in zusammengesetzten Texten */
const PARTS: [RegExp, string][] = [
  [/\(benötigt (\d+), frei (-?\d+)\)/g, "(needs $1, available $2)"],
  [/\(laut Lager nicht verfügbar\)/g, "(not available according to the warehouse)"],
];

type Compiled = { re: RegExp; en: string; fixed: number };
let compiled: Compiled[] | null = null;
let exact: Map<string, string> | null = null;

function compile() {
  const cat = getServerCatalog();
  exact = new Map();
  compiled = [];
  for (const [de, en] of Object.entries(cat)) {
    if (!en) continue;
    if (!/\{\d+\}/.test(de)) { exact.set(de, en); continue; }
    // direkt aufeinanderfolgende Platzhalter („fehlt in {0}{1}“): der zweite ist ein optionaler Zusatz,
    // der mit einem Trenner beginnt (" · 2 Stk. benötigt", ", 1 kritisch", " (…)")
    const parts = de.split(/(\{\d+\})/).filter((p) => p !== "");
    const isPh = (p: string | undefined) => !!p && /^\{\d+\}$/.test(p);
    const countPattern = /^\{0\} [A-Za-zÄÖÜäöüß]/.test(de) && de.replace(/\{\d+\}/g, "").trim().split(/\s+/).length <= 3;
    const src = parts.map((part, i) => {
      if (!isPh(part)) return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const name = `p${part.slice(1, -1)}`;
      // kurze Mengen-Muster („{0} prüfen“, „{0} überfällig“) nur mit Zahl vorne – sonst träfen sie Freitexte/Regelnamen der Kunden
      if (i === 0 && countPattern) return `(?<${name}>\\d[\\s\\S]*?)`;
      if (isPh(parts[i - 1])) return `(?<${name}>(?: ?[·,(;:–-][\\s\\S]*)?)`;
      return isPh(parts[i + 1]) ? `(?<${name}>[\\s\\S]+?)` : `(?<${name}>[\\s\\S]*?)`;
    }).join("");
    compiled.push({ re: new RegExp(`^${src}$`), en, fixed: de.replace(/\{\d+\}/g, "").length });
  }
  // spezifischere (längere feste Teile) zuerst
  compiled.sort((a, b) => b.fixed - a.fixed);
}

function translate(s: string, depth: number): string {
  if (!compiled) compile();
  const hit = exact!.get(s);
  if (hit) return hit;
  // auch Portal-Begriffe (z. B. ein eingesetzter Status „Geprüft“)
  if (depth > 0) { const ui = t(s); if (ui !== s) return ui; }
  for (const c of compiled!) {
    const m = c.re.exec(s);
    if (m) return c.en.replace(/\{(\d+)\}/g, (_, i: string) => {
      const v = m.groups?.[`p${i}`] ?? "";
      return depth < 2 ? translate(v, depth + 1) : v;
    });
  }
  for (const [re, en] of PREFIX) if (re.test(s)) return PARTS.reduce((acc, [p, r]) => acc.replace(p, r), s.replace(re, en));
  return s;
}

export function serverText(s: string): string;
export function serverText(s: string | null | undefined): string | null | undefined;
export function serverText(s: string | null | undefined) {
  if (getLocale() === "de" || !s) return s;
  return translate(s, 0);
}

registerServerFallback((s) => translate(s, 0));
